import { createContext, useContext, type ReactNode } from "react";
import type { CartItemWithPrice } from "@/types/api";
import useMusioApi from "@/contexts/client/musio-client-context";
import usePaddle from "@/contexts/paddle/paddle-context";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useCart } from "./cart-context";

type ProductContextType = {
  items: CartItemWithPrice[];
  loading: boolean;
  isError: boolean;
};

// NOTE: This is dependent on the following contexts:
// - paddle
// - api
// - cart

const ProductContext = createContext<ProductContextType>({ items: [], loading: true, isError: false });

export function ProductProvider({ children }: { children: ReactNode }) {
  const paddle = usePaddle();
  const { setCurrency } = useCart();
  const { musioApi } = useMusioApi();

  // TODO: consider incremental hydration here. maybe return the prodcuts and hydrate after the products
  const { data: marketplaceItems, isPending: isCartItemsPending, error: musioCartItemsError, isError: isMusioCartItemsError } = musioApi.useQuery("get", "/marketplace/cart-items");
  const { data: items = [], isFetching: isPriceIdsFetching, error: paddleError, isError: isPaddleError } = useQuery({
    queryKey: ['paddle-price-ids'],
    queryFn: async () => {
      if (!marketplaceItems || !paddle) { return };

      const priceIds = marketplaceItems.filter(i => i.priceId).map(i => ({ priceId: i.priceId!, quantity: 1 }));
      const salePriceIds = marketplaceItems.filter(i => i.salePriceId).map(i => ({ priceId: i.salePriceId!, quantity: 1 }));

      const [pricePreview, salePricePreview] = await Promise.all([
        priceIds.length ? paddle.PricePreview({ items: priceIds }) : Promise.resolve({ data: { details: { lineItems: [] }, currencyCode: "USD", } }),
        salePriceIds.length ? paddle.PricePreview({ items: salePriceIds }) : Promise.resolve({ data: { details: { lineItems: [] } } }),
      ]);

      setCurrency(pricePreview.data.currencyCode);

      const priceMap: Record<string, { formattedPrice: string; price: number }> = {};
      pricePreview.data.details.lineItems.forEach(item => {
        priceMap[item.price.id] = {
          formattedPrice: item.formattedTotals.subtotal, price: parseFloat(item.unitTotals.subtotal)
        };
      });

      const salePriceMap: Record<string, { formattedSalePrice: string; salePrice: number }> = {};
      salePricePreview.data.details.lineItems.forEach(item => {
        salePriceMap[item.price.id] = { formattedSalePrice: item.formattedTotals.subtotal, salePrice: parseFloat(item.unitTotals.subtotal) };
      });

      const enriched: CartItemWithPrice[] = marketplaceItems.map(item => ({
        ...item,
        formattedPrice: item.priceId ? priceMap[item.priceId]?.formattedPrice || "" : "",
        formattedSalePrice: item.salePriceId ? salePriceMap[item.salePriceId]?.formattedSalePrice || "" : "",
        price: item.priceId ? priceMap[item.priceId]?.price || 0 : 0,
        salePrice: item.salePriceId ? salePriceMap[item.salePriceId]?.salePrice || 0 : 0,
      }));

      return enriched;
    },
    enabled: !!marketplaceItems && !(marketplaceItems.length == 0),
    initialData: [],
  });

  // INFO: error logging if present. if the service fails or paddle fails this is a big issue
  isMusioCartItemsError && Sentry.captureException(musioCartItemsError.error, { data: "Captured in paddle-context.tsx" });
  isPaddleError && Sentry.captureException(paddleError.message, { data: "Captured in paddle-context.tsx" });

  return (
    <ProductContext.Provider value={{
      items, loading: isCartItemsPending || isPriceIdsFetching, isError: isMusioCartItemsError || isPaddleError,
    }}>
      {children}
    </ProductContext.Provider >
  );
}

export function useProducts() {
  const context = useContext(ProductContext);
  if (!context) throw new Error("useProducts must be used within a ProductProvider");
  return context;
}
